import React, { useEffect, useState } from "react";
import { Mail, Phone, MapPin, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { usePageSeo } from "@/lib/usePageSeo";
import { useJsonLd, buildLocalBusinessSchema } from "@/lib/seoSchema";
import { trackEvent } from "@/lib/analytics";
import { Reveal } from "@/components/util/Reveal";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const s = useSettings();
  // Website Design Pass 3D — the Advanced Project CTA on the Website
  // configurator routes here with ?topic=custom-website-quote so the
  // customer arrives with the intent already framed. Prefill message
  // once on mount so subsequent edits stay in the customer's control.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("topic") === "custom-website-quote") {
      setForm((prev) =>
        prev.message
          ? prev
          : {
              ...prev,
              message:
                "Custom Website Quote — I have a website project that may need custom functionality, integrations or a more specialized setup. Here's what I'm looking for:\n\n",
            }
      );
    }
  }, []);
  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = (e) => {
    e.preventDefault();
    toast.success("Thanks! We'll get back to you shortly.");
    trackEvent("contact_form_submit");
    setForm({ name: "", email: "", phone: "", message: "" });
  };

  const eyebrow = s.contact_eyebrow;
  const heading1 = s.contact_heading_line1;
  const heading2 = s.contact_heading_line2;
  const desc = s.contact_description;
  const email = s.contact_email;
  const phone = s.contact_phone;
  const address = s.contact_address;
  const accentInline = !!s.contact_accent_inline;
  const showDot = !!s.contact_show_dot;

  // SEO Phase 3 — exact metadata proposed to owner for review.
  usePageSeo({
    title: "Contact Ur Designs — Palm Beach, Aruba",
    description: `Get in touch with Ur Designs. Call ${phone} or email ${email}. Creative studio in Palm Beach, Aruba.`,
    path: "/contact",
  });
  useJsonLd(buildLocalBusinessSchema(s));

  return (
    <div data-testid="contact-page" className="bg-[#0F0F0F]">
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-24 grid lg:grid-cols-2 gap-12">
        <div>
          {/* Text block reveals as one unit. */}
          <Reveal>
            <div data-testid="contact-eyebrow" className="ur-eyebrow">{eyebrow}</div>
            <h1 data-testid="contact-heading" className="font-display mt-4 text-5xl sm:text-6xl font-black tracking-tight">
              {heading1}
              {accentInline
                ? <span className="text-[#FF6600]">{heading2}</span>
                : <> <span className="text-[#FF6600]">{heading2}</span></>}
            </h1>
            {showDot && <div className="mt-4 flex"><span className="w-2 h-2 rounded-full bg-[#FF6600]" data-testid="contact-dot" /></div>}
            <p data-testid="contact-description" className="mt-5 text-[#B5B5B5] max-w-md">
              {desc}
            </p>
          </Reveal>
          <Reveal delay={120} data-testid="contact-info-list" className="mt-10 space-y-5 text-sm">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-[#FF6600]/10 border border-[#FF6600]/40 grid place-items-center">
                <Mail className="w-4 h-4 text-[#FF6600]" />
              </div>
              {email}
            </div>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-[#FF6600]/10 border border-[#FF6600]/40 grid place-items-center">
                <Phone className="w-4 h-4 text-[#FF6600]" />
              </div>
              {phone}
            </div>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-[#FF6600]/10 border border-[#FF6600]/40 grid place-items-center">
                <MapPin className="w-4 h-4 text-[#FF6600]" />
              </div>
              {address}
            </div>
          </Reveal>
        </div>

        <form onSubmit={submit} data-testid="contact-form" className="ur-card p-8 space-y-5">
          <Field label="Your Name" value={form.name} onChange={handle("name")} testid="contact-name" required />
          <Field label="Email" type="email" value={form.email} onChange={handle("email")} testid="contact-email" required />
          <Field label="Phone (optional)" value={form.phone} onChange={handle("phone")} testid="contact-phone" />
          <div>
            <label className="text-xs uppercase tracking-widest text-[#A3A3A3]">Message</label>
            <textarea
              data-testid="contact-message"
              value={form.message}
              onChange={handle("message")}
              required
              rows={5}
              className="mt-2 w-full bg-[#0F0F0F] border border-[#262626] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#FF6600] resize-none"
            />
          </div>
          <button data-testid="contact-submit" type="submit" className="ur-btn-primary w-full">
            Send Message <ArrowRight className="w-4 h-4" />
          </button>
        </form>
      </section>
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text", testid, required }) => (
  <div>
    <label className="text-xs uppercase tracking-widest text-[#A3A3A3]">{label}</label>
    <input
      data-testid={testid}
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      className="mt-2 w-full bg-[#0F0F0F] border border-[#262626] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#FF6600]"
    />
  </div>
);
